import React, { useState, useEffect } from 'react';
import { getBalance } from '../../services/api';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const UserTrading = () => {
  const [balance, setBalance] = useState(0);
  const [formData, setFormData] = useState({
    coin_ticker: 'BTC',
    direction: 'LONG',
    amount_usdt: '',
    entry_price: '',
    exit_price: '',
  });
  const [tradeRequests, setTradeRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBalance();
    fetchTradeRequests();
  }, []);

  const fetchBalance = async () => {
    try {
      const response = await getBalance();
      if (response.data.success) setBalance(response.data.balance);
    } catch (error) {
      console.error('Failed to fetch balance');
    }
  };

  const fetchTradeRequests = async () => {
    try {
      const response = await fetch('http://localhost:5000/api/user/trade-requests', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) setTradeRequests(data.requests);
    } catch (error) {
      console.error('Failed to fetch trade requests');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount_usdt || parseFloat(formData.amount_usdt) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (parseFloat(formData.amount_usdt) > balance) {
      toast.error('Insufficient balance');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch('http://localhost:5000/api/user/trade-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          coin_ticker: formData.coin_ticker,
          direction: formData.direction,
          amount_usdt: parseFloat(formData.amount_usdt),
          entry_price: formData.entry_price || null,
          exit_price: formData.exit_price || null,
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Trade request submitted! Awaiting admin approval.');
        setFormData({ ...formData, amount_usdt: '', entry_price: '', exit_price: '' });
        fetchBalance();
        fetchTradeRequests();
      } else {
        toast.error(data.message || 'Failed to submit trade');
      }
    } catch (error) {
      toast.error('Failed to submit trade request');
    } finally {
      setLoading(false);
    }
  };

  const coinOptions = ['BTC', 'ETH', 'SOL', 'XRP', 'BNB'];

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-bold text-white">Trade with Admin Approval</h1>
        <div className="text-right">
          <p className="text-xs text-gray-400">Available Balance</p>
          <p className="text-lg font-bold text-emerald-500">{balance.toFixed(2)} USDT</p>
        </div>
      </div>
      
      {/* Warning Message */}
      <div className="card p-3 border-yellow-500/30 bg-yellow-500/5">
        <p className="text-xs text-yellow-500 text-center">
          ⚠️ User trades require admin approval. Negative PnL from user trades will be limited to protect your account.
        </p>
      </div>
      
      {/* Trade Form */}
      <div className="card p-5">
        <h2 className="text-base font-semibold text-white mb-4">Request Trade</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Coin</label>
              <select
                value={formData.coin_ticker}
                onChange={(e) => setFormData({ ...formData, coin_ticker: e.target.value })}
                className="input-dark w-full"
              >
                {coinOptions.map(coin => (
                  <option key={coin} value={coin}>{coin}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Direction</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, direction: 'LONG' })}
                  className={`flex-1 py-2 rounded-lg text-sm transition ${
                    formData.direction === 'LONG' ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  <ArrowTrendingUpIcon className="w-4 h-4 inline mr-1" />
                  LONG
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, direction: 'SHORT' })}
                  className={`flex-1 py-2 rounded-lg text-sm transition ${
                    formData.direction === 'SHORT' ? 'bg-red-600 text-white' : 'bg-gray-700 text-gray-300'
                  }`}
                >
                  <ArrowTrendingDownIcon className="w-4 h-4 inline mr-1" />
                  SHORT
                </button>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Amount (USDT)</label>
            <input
              type="number"
              value={formData.amount_usdt}
              onChange={(e) => setFormData({ ...formData, amount_usdt: e.target.value })}
              className="input-dark w-full"
              placeholder="Min: 10 USDT"
              step="0.01"
              min="10"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Entry Price (Optional)</label>
              <input
                type="number"
                value={formData.entry_price}
                onChange={(e) => setFormData({ ...formData, entry_price: e.target.value })}
                className="input-dark w-full"
                placeholder="e.g., 50000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Exit Price (Optional)</label>
              <input
                type="number"
                value={formData.exit_price}
                onChange={(e) => setFormData({ ...formData, exit_price: e.target.value })}
                className="input-dark w-full"
                placeholder="e.g., 51000"
              />
            </div>
          </div>
          
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Trade Request'}
          </button>
        </form>
      </div>
      
      {/* Trade Requests History */}
      <div className="card p-5">
        <h2 className="text-base font-semibold text-white mb-3">Your Trade Requests</h2>
        {tradeRequests.length === 0 ? (
          <p className="text-gray-500 text-center py-4 text-sm">No trade requests yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-dark w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Coin</th>
                  <th>Direction</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>PnL</th>
                </tr>
              </thead>
              <tbody>
                {tradeRequests.map((req) => (
                  <tr key={req.id}>
                    <td className="text-xs text-gray-400">{new Date(req.created_at).toLocaleString()}</td>
                    <td className="text-sm">{req.coin_ticker}</td>
                    <td>
                      <span className={`text-xs ${req.direction === 'LONG' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {req.direction}
                      </span>
                    </td>
                    <td className="text-sm">{parseFloat(req.amount_usdt).toFixed(2)} USDT</td>
                    <td>
                      <span className={`badge-${req.status === 'approved' ? 'success' : req.status === 'pending' ? 'warning' : 'info'}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className={`text-sm ${parseFloat(req.executed_pnl) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {req.executed_pnl ? `${parseFloat(req.executed_pnl) >= 0 ? '+' : ''}${parseFloat(req.executed_pnl).toFixed(2)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTrading;